import { Divider } from '@eventbrite/eds-divider';
import { Trending as TrendingSvg } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import { getCanonicalSearchUrl } from '@eventbrite/redux-destination';
import { useLayer } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { SEO_LINK_TO_CITY_BROWSE_FROM_DSRP_EXPERIMENT } from '../../../../constants/constants';
import { DiscoverState, PaginationData } from '../../../../types';
import { SearchPivotAnchor } from './components/SearchPivotAnchor';
import './SearchPivotsContainer.scss';
import { SearchPivotSuggestionsProps } from './SearchPivotSuggestionsContainer';
import { getTrendingSearchData } from './selectors';

const SearchPivotSuggestions: FunctionComponent<SearchPivotSuggestionsProps> =
    loadable(() => import('./SearchPivotSuggestionsContainer'));

/**
 * Suggested actions for the user to take given low, empty, or
 * end of search results.
 *
 * Always displays the trending searches footer.
 *
 * @param {*} props
 */
type SearchResultsPivotsProps = {
    isLoading: boolean;
    pagination: PaginationData;
};

type SearchResultsPivotReduxProps = {
    trendingSearchData: {
        path?: string;
        text?: object;
    };
    location?: {
        slug?: string;
        currentPlace?: string;
        currentPlaceParent?: string;
    };
};

/**
 * This function creates the title for the city browse link in
 * the footer of DSRP pages.
 *
 */
export const formatCityBrowseTitle = (
    currentPlace?: string,
    currentPlaceParent?: string,
) => {
    let text;

    if (currentPlace === 'Online Events') {
        text = gettext('Events Online');
    } else {
        text = gettext('Events in %(city)s', {
            city: currentPlace,
        });

        if (currentPlaceParent) {
            text = gettext('Events in %(city)s, %(region)s', {
                city: currentPlace,
                region: currentPlaceParent,
            });
        }
    }

    return text;
};

export const SearchResultsPivots = ({
    isLoading,
    pagination,
    trendingSearchData,
    location,
}: SearchResultsPivotsProps & SearchResultsPivotReduxProps) => {
    const isNextDisabled = pagination.page === pagination.pageCount;
    const hasEventResults = pagination.objectCount > 0;
    const shouldRenderSuggestions =
        !isLoading && (!hasEventResults || isNextDisabled);

    const { value } = useLayer(
        SEO_LINK_TO_CITY_BROWSE_FROM_DSRP_EXPERIMENT.layer,
        SEO_LINK_TO_CITY_BROWSE_FROM_DSRP_EXPERIMENT.param,
        SEO_LINK_TO_CITY_BROWSE_FROM_DSRP_EXPERIMENT.defaultValue,
    );

    const shouldShowCityBrowseLink = value && location;

    return (
        <div className="search-result-pivots">
            {shouldRenderSuggestions && (
                <SearchPivotSuggestions
                    isLoading={isLoading}
                    hasEventResults={hasEventResults}
                    isNextDisabled={isNextDisabled}
                />
            )}
            <div className="eds-l-pad-top-6 eds-l-pad-bot-10">
                <Divider color="ui-200" />
                {trendingSearchData && (
                    <SearchPivotAnchor
                        href={trendingSearchData.path}
                        text={trendingSearchData.text}
                        icon={<TrendingSvg />}
                    />
                )}
                {shouldShowCityBrowseLink && (
                    <SearchPivotAnchor
                        href={getCanonicalSearchUrl({
                            slug: location.slug,
                            moreEventsHomePage: true,
                        })}
                        text={formatCityBrowseTitle(
                            location.currentPlace,
                            location.currentPlaceParent,
                        )}
                    />
                )}
            </div>
        </div>
    );
};

const _mapStateToProps = (state: DiscoverState) => ({
    trendingSearchData: getTrendingSearchData(
        state.location,
        state.app.domain,
        state.shouldShowTrendingSearchPageLink,
    ),
    location: state.location,
});

export const SearchPivotsContainer =
    connect(_mapStateToProps)(SearchResultsPivots);
